<template>
  <div class="cesion-detail-page">
    <!-- Barra superior con acciones y estado -->
    <v-card class="mb-4 elevation-2">
      <v-app-bar flat dense color="primary" dark>
        <v-btn icon @click="$router.go(-1)">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title>Detalles de la Cesión</v-toolbar-title>
        <v-spacer></v-spacer>

        <v-chip v-if="!loading" small :color="getStatusColor(movimiento.status)">
          <v-icon left x-small>{{ getStatusIcon(movimiento.status) }}</v-icon>
          {{ translateStatus(movimiento.status) }}
        </v-chip>

        <v-btn v-if="!loading && canChangeStatus() && HAS_PERMISSION('aceptar_cesion')" small outlined color="white" class="ml-2" @click="acceptMovimiento">
          <v-icon left small>mdi-arrow-up-bold</v-icon>
          Avanzar estado
        </v-btn>
      </v-app-bar>
    </v-card>

    <!-- Contenido principal -->
    <v-container>
      <!-- Estado de carga -->
      <div v-if="loading" class="text-center py-8">
        <v-progress-circular indeterminate color="primary" size="50" width="5"></v-progress-circular>
        <div class="mt-4 text-subtitle-1 font-weight-medium grey--text text--darken-1">
          Cargando detalles de la cesión...
        </div>
      </div>

      <template v-else>
        <!-- Información del movimiento -->
        <v-card class="mb-4 elevation-1">
          <v-card-title class="d-flex align-center pb-1">
            <v-avatar color="primary lighten-4" size="42" class="mr-3">
              <v-icon color="primary">mdi-car</v-icon>
            </v-avatar>            <div>
              <div class="text-h5 font-weight-medium">
                {{ (movimiento.vehicle && movimiento.vehicle.brand) || 'Sin marca' }} {{ (movimiento.vehicle && movimiento.vehicle.model) || 'Sin modelo' }}
              </div>
              <div class="caption grey--text d-flex align-center">
                <v-icon x-small class="mr-1">mdi-calendar</v-icon>
                {{ movimiento.delivery_date ? formatDateHours(movimiento.delivery_date) : formatDateHours(movimiento.datetime) }}
                <v-icon x-small class="mx-1">mdi-circle-small</v-icon>
                {{ movimiento.type === 'entry' ? 'Entrada' : movimiento.type === 'assignment' ? 'Cesión' : movimiento.type === 'exit' ? 'Salida' : 'No especificado' }}
              </div>
            </div>
            <v-spacer></v-spacer>
            <div>
              <v-btn v-if="canEditMovimiento" icon small color="primary" @click="showMovimientoForm(movimiento)" class="mr-1"
                :disabled="movimiento.status === 'completed'">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn v-if="canDeleteMovimiento" icon small color="error" @click="confirmDeleteMovimiento"
                :disabled="movimiento.status === 'completed'">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </div>
          </v-card-title>

          <v-divider></v-divider>

          <!-- Trayecto (origen-destino) -->
          <v-card-text class="py-2">
            <div class="d-flex align-center">
              <v-icon color="primary" class="mr-2">mdi-map-marker-path</v-icon>
              <div class="text-body-1">
                <span class="font-weight-medium grey--text text--darken-1">{{ movimiento.sourceLocation ? movimiento.sourceLocation.name : 'No especificado' }}</span>
                <v-icon small class="mx-1">mdi-arrow-right</v-icon>
                <span class="font-weight-medium">{{ movimiento.destinationLocation ? movimiento.destinationLocation.name : 'No especificado' }}</span>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Pestañas para organizar la información -->
        <v-card class="elevation-1">
          <v-tabs v-model="activeTab" background-color="transparent" grow>
            <v-tab>
              <v-icon left>mdi-information-outline</v-icon>
              Detalles
            </v-tab>
            <v-tab>
              <v-icon left>mdi-car-info</v-icon>
              Vehículo
            </v-tab>
            <v-tab>
              <v-icon left>mdi-image-multiple</v-icon>
              Imágenes
              <v-badge v-if="allImages && allImages.length" color="primary"
                :content="allImages.length" offset-x="10" offset-y="-8" small></v-badge>
            </v-tab>
          </v-tabs>

          <v-divider></v-divider>

          <!-- Contenido de pestañas -->
          <v-tabs-items v-model="activeTab">
            <!-- Pestaña de detalles del movimiento -->
            <v-tab-item>
              <v-card flat>
                <v-card-text>
                  <v-row>
                    <!-- Columna izquierda -->
                    <v-col cols="12" md="6">
                      <v-simple-table dense>
                        <tbody>
                          <tr>
                            <td class="font-weight-medium" width="40%">
                              <v-icon small color="primary" class="mr-1">mdi-swap-horizontal-bold</v-icon>
                              Tipo
                            </td>
                            <td>
                              {{ movimiento.type === 'entry' ? 'Entrada' :
                                movimiento.type === 'assignment' ? 'Cesión' : 
                                movimiento.type === 'exit' ? 'Salida' : 'No especificado' }}
                            </td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">
                              <v-icon small color="primary" class="mr-1">mdi-truck-outline</v-icon>
                              Transporte
                            </td>
                            <td>
                              <template v-if="movimiento.transportation === 'DRIVER' || movimiento.transportation === 'CLIENTE'">
                              {{ movimiento.transportation || 'No especificado' }} - {{ movimiento.driver ? `${movimiento.driver.name || ''} ${movimiento.driver.lastname || ''}`.trim() : 'No especificado' }}
                              </template>
                              <template v-else-if="movimiento.transportation === 'GRUA'">
                              Grúa - {{ crane && crane.company ? crane.company : 'No especificada' }}
                              </template>
                              <template v-else>
                              {{ movimiento.transportation || 'No especificado' }}
                              </template>
                            </td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">
                              <v-icon small color="primary" class="mr-1">mdi-account-tie</v-icon>
                              Responsable de entrega
                            </td>
                            <td>
                                <template v-if="movimiento.responsibleEntrega">
                                {{ movimiento.responsibleEntrega.name }} {{ movimiento.responsibleEntrega.lastname }}
                                </template>
                                <template v-else>
                                No especificado
                                </template>
                            </td>
                          </tr>
                          <tr>
                              <td class="font-weight-medium">
                                <v-icon small color="primary" class="mr-1">mdi-account-arrow-right</v-icon>
                                Responsable recepción
                              </td>
                              <td>
                                <template v-if="movimiento.status === 'completed'">
                                  <span v-if="movimiento.responsible">
                                    {{ `${movimiento.responsible.name || ''} ${movimiento.responsible.lastname || ''}`.trim() || 'No especificado' }}
                                  </span>
                                  <span v-else>No especificado</span>
                                </template>
                                <template v-else>
                                  No especificado
                                </template>
                              </td>
                            </tr>
                          <tr v-if="movimiento.transportation === 'Driver'">
                            <td class="font-weight-medium">
                              <v-icon small color="primary" class="mr-1">mdi-account-card-details</v-icon>
                              Conductor
                            </td>
                            <td>DNI: {{ (movimiento.driver && movimiento.driver.document_number) || 'No especificado' }}</td>
                          </tr>                          <tr>
                            <td class="font-weight-medium">
                              <v-icon small color="primary" class="mr-1">mdi-speedometer</v-icon>
                              Kilómetros
                            </td>
                            <td>
                              <span>Iniciales: {{ movimiento.initial_km || 'No especificado' }}</span>
                              <span v-if="movimiento.end_km" class="ml-3">
                                | Finales: {{ movimiento.end_km }}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </v-col>

                    <!-- Columna derecha -->
                    <v-col cols="12" md="6">
                      <v-simple-table dense>
                        <tbody>
                          <tr>
                            <td class="font-weight-medium" width="40%">
                              <v-icon small :color="movimiento.vehicle_status === 'good' ? 'green' : 'red'"
                                class="mr-1">
                                {{ movimiento.vehicle_status === 'good' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                              </v-icon>
                              Estado del vehículo
                            </td>
                            <td>
                              {{ movimiento.vehicle_status === 'good' ? 'Bueno' : movimiento.vehicle_status === 'damages' ? 'Dañado' : 'No especificado' }}
                            </td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">
                              <v-icon small color="primary" class="mr-1">mdi-card-bulleted-outline</v-icon>
                              Documentación
                            </td>
                            <td>{{ movimiento.registered === 1 ? 'Matriculado' : movimiento.registered === 0 ? 'Placas Rojas' : 'No especificado' }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">
                              <v-icon small color="primary" class="mr-1">mdi-account-plus</v-icon>
                              Creado por
                            </td>
                            <td>
                              {{ creado && creado.data ? `${creado.data.name || ''} ${creado.data.lastname || ''}`.trim() : 'No especificado' }}
                              <small class="grey--text ml-1">{{ formatDate(movimiento.created_at) }}</small>
                            </td>
                          </tr>
                          <tr v-if="movimiento.type !== 'entry'">
                            <td class="font-weight-medium">
                              <v-icon small color="primary" class="mr-1">mdi-account-edit</v-icon>
                              Fecha de entrega
                            </td>
                            <td>
                              {{ movimiento.type === 'entry'
                                ? (movimiento.reception_datetime ? formatDate(movimiento.reception_datetime) : 'No especificado')
                                : (movimiento.delivery_date && (movimiento.status === 'processing' || movimiento.status === 'completed') ? formatDate(movimiento.delivery_date) : 'No especificado') }}
                            </td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">
                              <v-icon small color="primary" class="mr-1">mdi-update</v-icon>
                              Última actualización
                            </td>
                            <td>
                              {{ update && update.data ? `${update.data.name || ''} ${update.data.lastname || ''}`.trim() : 'No especificado' }}
                              <small class="grey--text ml-1">{{ formatDate(movimiento.updated_at) }}</small>
                            </td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </v-col>
                  </v-row>                  <!-- Observaciones -->
                  <div v-if="movimiento.observations" class="mt-4">
                    <v-divider class="mb-3"></v-divider>
                    <div class="font-weight-medium mb-2">
                      <v-icon small color="primary" class="mr-1">mdi-message-text</v-icon>
                      Observaciones
                    </div>
                    <div class="body-2 pa-2 grey lighten-4 rounded preserve-linebreaks">
                      {{ movimiento.observations }}
                    </div>
                  </div>

                  <!-- Información adicional -->
                  <div v-if="movimiento.additional_info || movimiento.additional_info_delivery || movimiento.additional_info_reception" class="mt-4">
                    <v-divider class="mb-3"></v-divider>
                    <div class="font-weight-medium mb-2">
                      <v-icon small color="primary" class="mr-1">mdi-information-outline</v-icon>
                      Información adicional
                    </div>
                    <div class="body-2 pa-2 blue lighten-5 rounded preserve-linebreaks">
                      <div v-if="movimiento.additional_info" class="mb-2">
                        <strong>CREACIÓN CESIÓN: </strong> {{ movimiento.additional_info }}
                      </div>
                      <div v-if="movimiento.additional_info_delivery" class="mb-2">
                        <strong>ENTREGA CESIÓN: </strong> {{ movimiento.additional_info_delivery }}
                      </div>
                      <div v-if="movimiento.additional_info_reception">
                        <strong>RECEPCIÓN CESIÓN: </strong> {{ movimiento.additional_info_reception }}
                      </div>
                    </div>
                  </div>

                  <!-- Equipamiento Básico -->
                  <div
                    v-if="movimiento.equipment_status && movimiento.equipment_status.some(equipment => ((equipment.is_available === false && equipment.changed === false) || (equipment.is_available === true && equipment.changed === true)) || equipment.is_available === false)"
                    class="mt-4"
                  >
                    <v-divider class="mb-3"></v-divider>

                    <div class="font-weight-medium mb-2">
                      <v-icon small color="primary" class="mr-1">
                        mdi-information-outline
                      </v-icon>
                      Equipamiento Básico
                    </div>

                    <div class="body-2 pa-2 blue lighten-5 rounded preserve-linebreaks">
                      <div>
                        Equipamiento básico no marcado: Entrega
                        <div
                          v-for="(equipment, index) in movimiento.equipment_status"
                          :key="index"
                        >
                          <div v-if="(equipment.is_available === false && equipment.changed === false) || (equipment.is_available === true && equipment.changed === true)">
                            - {{ equipment.equipment_name }}
                          </div>
                        </div>
                        <br>
                        <div v-if="movimiento.status === 'completed'">
                          Equipamiento básico no marcado: Recepción
                          <div
                            v-for="(equipment, index) in movimiento.equipment_status"
                            :key="index"
                          >
                            <div v-if="equipment.is_available === false">
                              - {{ equipment.equipment_name }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <!-- Pestaña de información del vehículo -->
            <v-tab-item>
              <v-card flat>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-simple-table dense>
                        <tbody>
                          <tr>
                            <td class="font-weight-medium" width="35%">
                              <v-icon small color="primary" class="mr-1">mdi-tag</v-icon>
                              Marca
                            </td>
                            <td>{{ (movimiento.vehicle && movimiento.vehicle.brand) || 'No especificado' }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">
                              <v-icon small color="primary" class="mr-1">mdi-car-side</v-icon>
                              Modelo
                            </td>
                            <td>{{ (movimiento.vehicle && movimiento.vehicle.model) || 'No especificado' }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">
                              <v-icon small color="primary" class="mr-1">mdi-card-bulleted</v-icon>
                              Matrícula
                            </td>
                            <td>{{ (movimiento.vehicle && movimiento.vehicle.plate_number) || 'No especificado' }}</td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-simple-table dense>
                        <tbody>
                          <tr>
                            <td class="font-weight-medium" width="35%">
                              <v-icon small color="primary" class="mr-1">mdi-card-account-details</v-icon>
                              Número IIC
                            </td>
                            <td>{{ (movimiento.vehicle && movimiento.vehicle.iic_number) || 'No especificado' }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">
                              <v-icon small color="primary" class="mr-1">mdi-identifier</v-icon>
                              Bastidor
                            </td>
                            <td>{{ (movimiento.vehicle && movimiento.vehicle.frame_number) || 'No especificado' }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">
                              <v-icon small color="primary" class="mr-1">mdi-car-info</v-icon>
                              Ver ficha completa
                            </td>
                            <td>
                              <v-btn x-small outlined color="primary"
                                :to="{ name: 'VehiculoDetail', params: { id: movimiento.vehicle.id } }">
                                Ver vehículo
                              </v-btn>
                            </td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <!-- Pestaña de imágenes -->
            <v-tab-item>
              <v-card flat>
                <v-card-text>
                  <!-- Imágenes de daños y adicionales combinadas -->
                  <div v-if="allImages && allImages.length > 0">
                    <v-row>
                      <!-- Imágenes de daños -->
                      <v-col v-for="(image, i) in movimiento.imagesUrls" :key="`damage-${i}`" cols="12" sm="6" md="4" lg="3">
                        <v-card class="mx-auto" outlined hover @click="openImageDialog(image.url)">
                          <v-img :src="image.url" height="150" cover class="cursor-pointer">
                            <template v-slot:placeholder>
                              <v-row class="fill-height ma-0" align="center" justify="center">
                                <v-progress-circular indeterminate color="primary"></v-progress-circular>
                              </v-row>
                            </template>
                            <div class="image-overlay d-flex align-center justify-center">
                              <v-icon color="white">mdi-magnify-plus</v-icon>
                            </div>
                          </v-img>
                          <!-- Chip para imágenes de daños según type -->
                          <v-chip class="image-badge" x-small color="error" text-color="white">
                            <v-icon left x-small>
                              {{ image.type === 'entrega' ? 'mdi-arrow-up-bold' : 'mdi-arrow-down-bold' }}
                            </v-icon>
                            {{ image.type === 'entrega' ? 'Entrega' : image.type === 'recepcion' ? 'Recepción' : 'Daño' }}
                          </v-chip>
                        </v-card>
                      </v-col>
                      
                      <!-- Imágenes adicionales -->
                      <v-col v-for="(image, i) in additionalImagesArray" :key="`additional-${i}`" cols="12" sm="6" md="4" lg="3">
                        <v-card class="mx-auto" outlined hover @click="openImageDialog(image.url)">
                          <v-img :src="image.url" height="150" cover class="cursor-pointer">
                            <template v-slot:placeholder>
                              <v-row class="fill-height ma-0" align="center" justify="center">
                                <v-progress-circular indeterminate color="primary"></v-progress-circular>
                              </v-row>
                            </template>
                            <div class="image-overlay d-flex align-center justify-center">
                              <v-icon color="white">mdi-magnify-plus</v-icon>
                            </div>
                          </v-img>
                          <!-- Chip para imágenes adicionales según type -->
                          <v-chip class="image-badge" x-small color="success" text-color="white">
                            <v-icon left x-small>
                              {{ image.type === 'entrega' ? 'mdi-arrow-up-bold' : 'mdi-arrow-down-bold' }}
                            </v-icon>
                            {{ image.type === 'entrega' ? 'Entrega' : image.type === 'recepcion' ? 'Recepción' : 'Entrega' }}
                          </v-chip>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>
                  <div v-else class="text-center py-8 grey--text">
                    <v-icon size="50" color="grey lighten-2">mdi-image-off</v-icon>
                    <div class="mt-2">No hay imágenes disponibles</div>
                  </div>
                </v-card-text>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </template>
    </v-container>

    <!-- Componentes flotantes -->
    <!-- Formulario de edición -->
    <cesion-form v-if="visibleMovimientoForm" v-model="editItem" @save="saveMovimiento"
      @cancel="visibleMovimientoForm = false"></cesion-form>

    <!-- Diálogo de confirmación de eliminación -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="error--text">
          <v-icon left color="error">mdi-alert-circle</v-icon>
          Eliminar cesión
        </v-card-title>
        <v-card-text>
          ¿Está seguro que desea eliminar esta cesión? Esta acción no se puede deshacer.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Cancelar</v-btn>
          <v-btn color="error" @click="deleteMovimiento">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Visor de imágenes a pantalla completa -->
    <v-dialog v-model="imageViewerVisible" fullscreen>
      <v-card class="image-viewer">
        <v-btn icon class="close-button" @click="imageViewerVisible = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>

        <v-carousel v-if="allImages.length > 0" height="100vh" hide-delimiters :value="currentImageIndex"
          @input="currentImageIndex = $event">
          <v-carousel-item v-for="(image, i) in allImages" :key="`image-${i}`" :src="image.url"
            contain>
            <template v-slot:default>
              <div class="image-info pa-4">
                <v-chip small :color="image.type === 'damage' ? 'error' : 'success'" dark>
                  {{ image.type === 'damage' ? 'Daños' : 'Adicional' }}
                </v-chip>
              </div>
            </template>
          </v-carousel-item>
        </v-carousel>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import MovimientoService from '@/services/MovimientoService'
import UserService from '@/services/UserService'
import moment from 'moment-timezone'
import CesionForm from './CesionForm.vue'
import GruaService from '../../services/GruaService'
import { mapGetters } from 'vuex'

export default {
  components: {
    CesionForm
  },

  data () {
    return {
      loading: true,
      movimiento: null,
      creado: null,
      update: null,
      crane: null,
      visibleMovimientoForm: false,
      editItem: null,
      deleteDialog: false,
      imageViewerVisible: false,
      currentImageIndex: 0,
      activeTab: 0
    }
  },

  async created () {
    await this.loadMovimientoDetails()
  },

  methods: {
    async loadMovimientoDetails () {
      try {
        this.loading = true
        const moviId = this.$route.params.cesion_id
        const { data } = await MovimientoService.view({ id: moviId })
        this.movimiento = data.data

        // Cargar datos de usuarios
        const creadoData = await UserService.view({ id: this.movimiento.created_by })
        this.creado = creadoData.data
        if (this.movimiento.crane_id) {
          const grua = await GruaService.view({ id: this.movimiento.crane_id })
          this.crane = grua.data.data
        }
        const updateData = await UserService.view({ id: this.movimiento.updated_by })
        this.update = updateData.data
      } catch (error) {
        console.error('Error al cargar los detalles de la cesión:', error)
        this.$toast.error('Error al cargar los detalles de la cesión')
      } finally {
        this.loading = false
      }
    },
 formatDateHours (timestamp) {
      if (!timestamp) return 'N/A'
      return moment.unix(timestamp).tz('Europe/Madrid').format('DD/MM/YYYY HH:mm')
    },
    showMovimientoForm (item) {
      this.editItem = item
      this.visibleMovimientoForm = true
    },

    saveMovimiento () {
      this.visibleMovimientoForm = false
      this.loadMovimientoDetails()
      this.$toast.success('Cesión actualizado con éxito')
    },

    confirmDeleteMovimiento () {
      this.deleteDialog = true
    },

    async deleteMovimiento  () {
      try {
        this.deleteDialog = false
        const moviId = this.$route.params.movimiento_id
        await MovimientoService.delete({ id: moviId }).then((response) => {
          if (response.error) {
            this.$toast.error('Error al eliminar la cesión')
            return
          }
          this.$toast.success('Cesión eliminado con éxito')
          this.$router.push({ name: 'Cesion' })
        })
      } catch (error) {
        console.error('Error al eliminar la cesión:', error)
        this.$toast.error('Error al eliminar la cesión')
      }
    },

    async acceptMovimiento () {
      try {
        const moviId = this.$route.params.movimiento_id
        await MovimientoService.aceptarMovimiento(moviId).then((response) => {
          if (response.error) {
            this.$toast.error('Ocurrió un error al cambiar el estado de la cesión')
            return
          }
          this.$toast.success('Estado de la cesión actualizado con éxito')
          this.loadMovimientoDetails()
        })
      } catch (error) {
        console.error('Error al cambiar el estado de la cesión:', error)
        this.$toast.error('Ocurrió un error al cambiar el estado de la cesión')
      }
    },

    getCurrentUserRoles () {
      return this.$store.getters.CURRENT_USER_ROLES || []
    },

    canChangeStatus () {
      if (!this.movimiento) return false
      if (this.movimiento.status !== 'unreported') return false

      return this.HAS_PERMISSION('aceptar_cesion')
    },

    formatDate (timestamp) {
      if (!timestamp) return 'N/A'
      return moment.unix(timestamp).tz('Europe/Madrid').format('DD/MM/YYYY HH:mm')
    },

    translateStatus (status) {
      const statusMap = {
        'unreported': 'No reportado',
        'reported': 'Reportado',
        'transport': 'Recepcionado',
        'processing': 'En proceso',
        'completed': 'Completado'
      }
      return statusMap[status] || status
    },

    getStatusColor (status) {
      const statusColorMap = {
        'unreported': 'red',
        'reported': 'orange',
        'transport': 'blue',
        'processing': 'purple',
        'completed': 'green'
      }
      return statusColorMap[status] || 'grey'
    },

    getStatusIcon (status) {
      const statusIconMap = {
        'unreported': 'mdi-alert-circle-outline',
        'reported': 'mdi-bell-ring-outline',
        'transport': 'mdi-truck-outline',
        'processing': 'mdi-progress-clock',
        'completed': 'mdi-check-circle-outline'
      }
      return statusIconMap[status] || 'mdi-help-circle-outline'
    },

    openImageViewer (index) {
      this.currentImageIndex = index
      this.imageViewerVisible = true
    },

    openImageDialog (imageUrl) {
      // Encontrar el índice de la imagen en el array combinado
      const index = this.allImages.findIndex(img => img.url === imageUrl)
      if (index !== -1) {
        this.currentImageIndex = index
        this.imageViewerVisible = true
      }
    }
  },

  computed: {
    allImages () {
      const images = []
      
      // Agregar imágenes de daños
      if (this.movimiento && this.movimiento.imagesUrls) {
        // Verificar si es un array o un objeto
        if (Array.isArray(this.movimiento.imagesUrls)) {
          this.movimiento.imagesUrls.forEach(img => {
            images.push({
              ...img,
              type: 'damage'
            })
          })
        } else if (typeof this.movimiento.imagesUrls === 'object') {
          // Si es un objeto, convertir a array usando Object.values()
          Object.values(this.movimiento.imagesUrls).forEach(img => {
            if (img && img.url) {
              images.push({
                ...img,
                type: 'damage'
              })
            }
          })
        }
      }
      
      // Agregar imágenes adicionales
      if (this.movimiento && this.movimiento.additionalImagesUrls) {
        // Verificar si es un array o un objeto
        if (Array.isArray(this.movimiento.additionalImagesUrls)) {
          // Si es un array, usar forEach normalmente
          this.movimiento.additionalImagesUrls.forEach(img => {
            images.push({
              ...img,
              type: 'additional'
            })
          })
        } else if (typeof this.movimiento.additionalImagesUrls === 'object') {
          // Si es un objeto, convertir a array usando Object.values()
          Object.values(this.movimiento.additionalImagesUrls).forEach(img => {
            if (img && img.url) {
              images.push({
                ...img,
                type: 'additional'
              })
            }
          })
        }
      }
      
      return images
    },
    
    additionalImagesArray () {
      if (!this.movimiento || !this.movimiento.additionalImagesUrls) {
        return []
      }
      
      // Verificar si es un array o un objeto
      if (Array.isArray(this.movimiento.additionalImagesUrls)) {
        return this.movimiento.additionalImagesUrls
      } else {
        // Si es un objeto, convertir a array usando Object.values()
        return Object.values(this.movimiento.additionalImagesUrls)
      }
    },

    ...mapGetters(['HAS_PERMISSION']),

    canEditMovimiento() {
      return this.HAS_PERMISSION('editar_movimiento')
    },

    canDeleteMovimiento() {
      return this.HAS_PERMISSION('eliminar_movimiento')
    }
  }
}
</script>

<style scoped>
.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.2);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-badge {
  position: absolute !important;
  top: 8px;
  right: 8px;
  z-index: 1;
}

.v-img:hover .image-overlay {
  opacity: 1;
}

.cursor-pointer {
  cursor: pointer;
}

.image-viewer {
  background-color: rgba(0, 0, 0, 0.95);
}

.image-info {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
}

.close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  background-color: rgba(255, 255, 255, 0.2) !important;
  color: white !important;
}

.v-data-table td {
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

.preserve-linebreaks {
  white-space: pre-wrap;
  word-wrap: break-word;
}
</style>
